import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import { theme } from '@Guidelines/Theme/theme';
import type { DocumentListReturn } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentListReturn';
import type { ReviewStage } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useReviewStage';
import useDocumentViewer from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/useDocumentViewer';
import DeleteDocumentAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/DocumentActions/DeleteDocumentAction';
import DownloadDocumentAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/DocumentActions/DownloadDocumentAction';
import ValidateDocumentAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Shared/DocumentActions/ValidateDocumentAction';
import { css } from '@emotion/react';
import { ChevronLeft, ChevronRight, Ellipsis } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

const BAR_HEIGHT = '4.5rem';

const spacerStyle = css`
	height: ${BAR_HEIGHT};
`;

const barStyle = css`
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	min-height: ${BAR_HEIGHT};
	box-sizing: border-box;
	padding: 0.625rem 0.875rem calc(0.625rem + env(safe-area-inset-bottom));
	background-color: ${theme.colors.surface};
	border-top: 1px solid ${theme.colors.borderStrong};
`;

const growStyle = css`
	flex: 1;
	min-width: 0;
`;

const backdropStyle = css`
	position: fixed;
	inset: 0;
	z-index: 49;
`;

const menuStyle = css`
	margin: 0;
	position: absolute;
	right: 0.875rem;
	bottom: calc(100% + 0.375rem);
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	min-width: 14rem;
	padding: 0.375rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.borderStrong};
`;

const hiddenStyle = css`
	display: none;
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly reviewStage: ReviewStage | null;
	readonly listReturn: DocumentListReturn | null;
};

/**
 * Phone only: the fixed bottom bar — « ‹ » (previous field to do), « Prochain à vérifier », « Valider »
 * and `⋯` (Télécharger, Supprimer) — followed by a spacer as tall as the bar. The menu stays mounted
 * while closed so a confirmation opened by one of its actions outlives the menu.
 */
const MobileActionBar: React.FC<Props> = ({ subject, reviewStage, listReturn }) => {
	const isCompact = useIsCompactViewport();
	const { nextPendingField, goToAdjacentPending } = useDocumentViewer();

	const [isMenuOpen, setIsMenuOpen] = React.useState(false);

	const handlePrevious = React.useCallback(() => {
		goToAdjacentPending('previous');
	}, [goToAdjacentPending]);

	const handleNext = React.useCallback(() => {
		goToAdjacentPending('next');
	}, [goToAdjacentPending]);

	const handleToggleMenu = React.useCallback(() => {
		setIsMenuOpen((isOpen) => !isOpen);
	}, []);

	const handleCloseMenu = React.useCallback(() => {
		setIsMenuOpen(false);
	}, []);

	const handleMenuKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLFieldSetElement>) => {
		if (event.key === 'Escape') {
			setIsMenuOpen(false);
		}
	}, []);

	if (!isCompact) {
		return null;
	}

	return (
		<React.Fragment>
			<div css={spacerStyle} aria-hidden="true" />
			<div css={barStyle} role="toolbar" aria-label="Actions du document">
				<Button
					type="button"
					outlined
					severity="secondary"
					aria-label="Champ précédent"
					icon={<ChevronLeft size={16} aria-hidden="true" />}
					disabled={nextPendingField === null}
					onClick={handlePrevious}
				/>
				<div css={growStyle}>
					<Button
						type="button"
						outlined
						severity="secondary"
						aria-label="Prochain à vérifier"
						icon={<ChevronRight size={16} aria-hidden="true" />}
						disabled={nextPendingField === null}
						onClick={handleNext}
					/>
				</div>
				<ValidateDocumentAction subject={subject} reviewStage={reviewStage} listReturn={listReturn} />
				<Button
					type="button"
					outlined
					rounded
					severity="secondary"
					aria-label="Autres actions"
					aria-haspopup="menu"
					aria-expanded={isMenuOpen}
					icon={<Ellipsis size={16} aria-hidden="true" />}
					onClick={handleToggleMenu}
				/>
				<div css={isMenuOpen ? undefined : hiddenStyle}>
					<div css={backdropStyle} onClick={handleCloseMenu} aria-hidden="true" />
					<fieldset
						css={menuStyle}
						aria-label="Autres actions"
						onClick={handleCloseMenu}
						onKeyDown={handleMenuKeyDown}
					>
						<DownloadDocumentAction />
						<DeleteDocumentAction />
					</fieldset>
				</div>
			</div>
		</React.Fragment>
	);
};

export default MobileActionBar;
